// Laedt hls.js bei Bedarf und spielt HLS-Streams damit ab.
//
// hls.js liegt NICHT im vendor.js, sondern als eigene Datei unter
// /static/lib/hls.min.js (kopiert beim Bauen, siehe build/webpack.*.conf.js).
// Rund 400 KB, die nur gebraucht werden, wenn wirklich ein HLS-Stream laeuft -
// im vendor.js muesste jeder TV sie bei jedem Start mitladen. Geladen wird
// per <script> wie idcap.js, mit der Version als Cache-Buster.
//
// Die Adresse kommt aus process.env.HOST: die App laeuft auf Samsung im
// Dokument des Loaders (tizenStartapp), relative Pfade zeigten dort ins WGT.

import hlsPackage from 'hls.js/package.json';

const HLS_VERSION = hlsPackage.version;

const HLS_URL = String(process.env.HOST || '') + '/static/lib/hls.min.js?v=' + HLS_VERSION;
const LOAD_TIMEOUT_MS = 15000;
// So oft versucht hls.js nach einem Netzfehler weiterzuladen, bevor es aufgibt.
const NETWORK_RETRIES = 3;

let loading = null;

// Ist die Adresse ein HLS-Stream? Endung .m3u8, die Abfrage ignoriert.
export function isHlsUrl(url) {
  const path = String(url || '').split('?')[0].split('#')[0].toLowerCase();
  return /\.m3u8$/.test(path);
}

// Liefert die Hls-Klasse oder null, wenn sie nicht ladbar ist. Nie reject.
export function loadHls() {
  if (typeof window !== 'undefined' && window.Hls) return Promise.resolve(window.Hls);
  if (loading) return loading;
  loading = new Promise((resolve) => {
    let done = false;
    const finish = (hls) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      if (!hls) loading = null; // naechster Versuch darf neu laden
      resolve(hls);
    };
    const timer = setTimeout(() => {
      console.log('[HLS] hls.js nicht innerhalb von ' + LOAD_TIMEOUT_MS + ' ms geladen');
      finish(null);
    }, LOAD_TIMEOUT_MS);
    try {
      const s = document.createElement('script');
      s.src = HLS_URL;
      s.type = 'text/javascript';
      s.onload = () => {
        console.log('[HLS] hls.js ' + ((window.Hls && window.Hls.version) || '?') + ' geladen');
        finish(window.Hls || null);
      };
      s.onerror = () => {
        console.log('[HLS] hls.js nicht ladbar: ' + HLS_URL);
        finish(null);
      };
      document.head.appendChild(s);
    } catch (e) {
      console.log('[HLS] Laden fehlgeschlagen', e);
      finish(null);
    }
  });
  return loading;
}

// Kann das <video>-Element HLS selbst? (Tizen, webOS, Safari)
export function canPlayHlsNatively(video) {
  try {
    return !!(video && video.canPlayType && video.canPlayType('application/vnd.apple.mpegurl'));
  } catch (e) {
    return false;
  }
}

// Haengt einen HLS-Stream an ein <video>-Element.
//
// Weg: hls.js, wo es laeuft (Media Source Extensions) - das ist der Normalfall,
// auch auf den TVs. Kann der Browser kein MSE oder ist hls.js nicht ladbar,
// bleibt das native HLS des Elements (Tizen und webOS koennen es von Haus aus).
// Bricht hls.js mit einem fatalen Fehler ab, der sich nicht beheben laesst,
// wird ebenfalls auf das native HLS umgeschaltet, sofern das Element es kann.
//
// Liefert ein Promise auf { mode: 'hlsjs' | 'native' | 'none', destroy() }.
export function attachHls(video, url) {
  const nativeOk = canPlayHlsNatively(video);
  const useNative = (why) => {
    console.log('[HLS] natives HLS (' + why + '): ' + url);
    if (nativeOk) {
      video.setAttribute('src', url);
      try {
        video.load();
      } catch (e) {
        /* aeltere webOS-Staende - das src reicht */
      }
    }
    return { mode: nativeOk ? 'native' : 'none', destroy() {} };
  };

  return loadHls().then((Hls) => {
    if (!Hls || !Hls.isSupported()) return useNative(Hls ? 'kein MSE' : 'hls.js fehlt');

    const hls = new Hls({
      // Kein Web Worker: auf den TV-Engines teils unzuverlaessig, und der
      // Gewinn ist bei einem einzelnen Stream klein.
      enableWorker: false,
      // Live: nicht zu nah an der Kante, sonst puffert es bei jedem Ruckler.
      liveSyncDurationCount: 3,
    });
    let switched = false;
    let networkRetries = 0;
    hls.on(Hls.Events.ERROR, (event, data) => {
      if (!data || !data.fatal) return;
      console.log('[HLS] fataler Fehler: ' + data.type + ' / ' + data.details);
      // Erst selbst heilen, wie hls.js es empfiehlt - bei Netzfehlern aber
      // hoechstens NETWORK_RETRIES Mal. Laesst sich schon das Manifest nicht
      // laden (z.B. http-Stream, den der Browser in einer https-Seite
      // blockiert), sofort aufs native HLS: das Element laedt selbst und darf
      // es je nach Engine.
      const manifestFailed = /^manifest/i.test(String(data.details || ''));
      if (data.type === Hls.ErrorTypes.NETWORK_ERROR && !manifestFailed && networkRetries < NETWORK_RETRIES) {
        networkRetries += 1;
        hls.startLoad();
        return;
      }
      if (data.type === Hls.ErrorTypes.MEDIA_ERROR && !hls.__recovered) {
        hls.__recovered = true;
        hls.recoverMediaError();
        return;
      }
      // Nicht zu retten: auf natives HLS ausweichen, wenn es das gibt.
      if (!switched && nativeOk) {
        switched = true;
        hls.destroy();
        useNative('Rueckfall nach Fehler');
      }
    });
    hls.loadSource(url);
    hls.attachMedia(video);
    console.log('[HLS] hls.js spielt: ' + url);
    return {
      mode: 'hlsjs',
      destroy() {
        try {
          hls.destroy();
        } catch (e) {
          /* schon abgebaut */
        }
      },
    };
  });
}



// WEBPACK FOOTER //
// ./src/helpers/HlsLoader.js